import { useCallback, useEffect, useState } from 'react'
import { AppLayout } from '../components/AppLayout'
import { sellerApi, type SellerOrderRow } from '../api/endpoints'
import { ApiError, type FulfillmentStatus } from '../api/types'
import { useApp } from '../store'
import { toast } from '../lib/toast'
import { formatPrice, formatCarat } from '../lib/format'

/**
 * /seller/orders — supplier-side order management.
 *
 * A supplier manages THEIR OWN concierge orders end to end:
 *   Confirm order (paid→processing) · Mark shipped (processing→shipped, with
 *   carrier + tracking) · Mark delivered (shipped→delivered).
 *
 * Backed by /seller/orders/* — supplier/admin only, re-scoped on the token, so a
 * pure buyer sees the 403 empty state and a supplier only ever sees their own
 * orders + their own lines. Reuses the SAME shared fulfillment state machine +
 * notifications as the admin board; the buyer gets the same status emails.
 * Visual language mirrors AdminOrders (badges, ship form, error-code → message
 * mapping).
 */

const STATUS_LABEL: Record<string, string> = {
  pending_payment: 'Pending payment',
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function statusPillClasses(status: string | null | undefined): string {
  switch (status) {
    case 'delivered':
      return 'bg-success/10 text-success border-success/30'
    case 'shipped':
    case 'processing':
    case 'paid':
      return 'bg-primary/10 text-primary border-primary/30'
    case 'cancelled':
    case 'refunded':
      return 'bg-danger/10 text-danger border-danger/30'
    case 'pending_payment':
    default:
      return 'bg-warn/10 text-warn border-warn/30'
  }
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  return (
    <span
      data-testid="seller-order-status"
      className={`inline-flex items-center rounded-full border px-2 h-6 text-[10px] font-bold uppercase tracking-wider ${statusPillClasses(status)}`}
    >
      {status ? (STATUS_LABEL[status] || status) : '—'}
    </span>
  )
}

/** Map a backend transition error to a friendly message (mirror of AdminOrders). */
function transitionErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { code?: string; from?: string; to?: string } | undefined
    if (body?.code === 'invalid_transition') {
      return `Can't move from ${STATUS_LABEL[body.from || ''] || body.from} to ${STATUS_LABEL[body.to || ''] || body.to}.`
    }
    if (body?.code === 'tracking_required') {
      return 'Carrier and tracking number are required to mark shipped.'
    }
    if (body?.code === 'forbidden_status' || body?.code === 'forbidden') {
      return 'You are not allowed to make that change on this order.'
    }
    if (body?.code === 'multi_supplier_order') {
      return 'This order includes items from another supplier — our team coordinates its fulfillment.'
    }
    return e.message
  }
  return e instanceof Error ? e.message : 'Status update failed'
}

export function SellerOrders() {
  const signedIn = useApp((s) => s.signedIn)
  const [orders, setOrders] = useState<SellerOrderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await sellerApi.orders({ limit: 200 })
      setOrders(r.orders)
      setError(null)
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setError('Supplier or admin access required.')
      else setError(e instanceof ApiError ? e.message : 'Failed to load orders')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (signedIn) load()
    else { setLoading(false); setError('Sign in as a supplier to manage your orders.') }
  }, [signedIn, load])

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Supplier</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-ink">Orders</h1>
        <p className="mt-2 text-sm text-muted max-w-xl">
          Orders placed for your stones through Aska Services land here. Confirm each order, ship
          it, and mark it delivered — the buyer is emailed at every step.
        </p>

        {loading ? (
          <div className="mt-8 space-y-3">
            <div className="h-32 rounded-card skeleton" />
            <div className="h-32 rounded-card skeleton" />
          </div>
        ) : error ? (
          <div className="mt-8 rounded-card border border-danger/30 bg-danger/5 p-4 text-sm text-danger">{error}</div>
        ) : orders.length === 0 ? (
          <div className="mt-8 rounded-card border border-border bg-page p-8 text-center">
            <p className="text-base font-semibold text-ink">No orders yet</p>
            <p className="mt-1 text-sm text-muted">Orders appear here as buyers buy your stones through Aska Services.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {orders.map((o) => (
              <OrderCard key={o.orderId} order={o} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}

/** One supplier order: scoped lines + subtotal + the confirm/ship/deliver actions. */
function OrderCard({ order, onChanged }: { order: SellerOrderRow; onChanged: () => void }) {
  const [busy, setBusy] = useState<FulfillmentStatus | null>(null)
  const [showShipForm, setShowShipForm] = useState(false)
  const [carrier, setCarrier] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [trackingUrl, setTrackingUrl] = useState('')

  const status = order.fulfillment.status
  const subtotal = order.supplierSubtotal ?? order.total

  async function advance(to: FulfillmentStatus, opts?: { carrier?: string; trackingNumber?: string; trackingUrl?: string }) {
    setBusy(to)
    try {
      await sellerApi.setOrderStatus(order.orderId, {
        to,
        carrier: opts?.carrier ?? null,
        trackingNumber: opts?.trackingNumber ?? null,
        trackingUrl: opts?.trackingUrl ?? null,
      })
      setShowShipForm(false)
      setCarrier(''); setTrackingNumber(''); setTrackingUrl('')
      toast(
        to === 'processing' ? 'Order confirmed' : to === 'shipped' ? 'Order marked shipped' : 'Order marked delivered',
        'success',
      )
      onChanged()
    } catch (e) {
      toast(transitionErrorMessage(e), 'error')
    } finally {
      setBusy(null)
    }
  }

  function submitShip(e: React.FormEvent) {
    e.preventDefault()
    if (!carrier.trim() || !trackingNumber.trim()) {
      toast('Carrier and tracking number are required to mark shipped.', 'error')
      return
    }
    void advance('shipped', {
      carrier: carrier.trim(),
      trackingNumber: trackingNumber.trim(),
      trackingUrl: trackingUrl.trim() || undefined,
    })
  }

  const f = order.fulfillment
  const hasTracking = !!(f.carrier || f.trackingNumber)

  return (
    <div className="card" data-testid="seller-order-row" data-order-id={order.orderId}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-bold text-ink">#{order.orderId}</p>
          <p className="mt-0.5 text-xs text-muted">{fmtDate(order.orderDate)}</p>
          <p className="mt-1 text-sm text-ink truncate">{order.buyer.email || '—'}</p>
          {order.buyer.name && <p className="text-xs text-muted truncate">{order.buyer.name}</p>}
        </div>
        <div className="text-right shrink-0">
          <StatusBadge status={status} />
          <p className="mt-2 text-sm font-bold text-ink tabular-nums">{formatPrice(subtotal)}</p>
          <p className="text-[10px] uppercase tracking-wider text-soft">Your subtotal</p>
        </div>
      </div>

      {/* This supplier's lines */}
      <ul className="mt-4 divide-y divide-border rounded-xl border border-border">
        {order.lines.map((l, i) => (
          <li key={`${l.stockId ?? 'line'}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">#{l.stockId || '—'}</p>
              <p className="text-xs text-soft">
                {[l.shape, l.carat != null ? formatCarat(l.carat) : null].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
            <p className="shrink-0 text-sm font-semibold text-ink tabular-nums">{formatPrice(l.amount)}</p>
          </li>
        ))}
      </ul>

      {/* Tracking summary (when set) */}
      {hasTracking && (
        <div className="mt-4 rounded-xl border border-border p-3 text-sm">
          <p className="text-[10px] uppercase tracking-wider text-soft mb-1">Shipment</p>
          <p className="text-ink">
            {f.carrier || '—'}
            {f.trackingNumber && <span className="text-muted"> · {f.trackingNumber}</span>}
          </p>
          {f.trackingUrl && (
            <a href={f.trackingUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
              Track shipment →
            </a>
          )}
        </div>
      )}

      {/* Ship form — carrier + tracking (required) + optional URL */}
      {showShipForm && (
        <form onSubmit={submitShip} className="mt-4 rounded-xl border border-border bg-page/50 p-3 space-y-2">
          <p className="text-xs font-semibold text-ink">Shipping details</p>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-soft">Carrier</span>
            <input
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="e.g. FedEx, DHL, Malca-Amit"
              className="input mt-1 h-9 w-full px-3 text-sm"
              data-testid="seller-order-carrier-input"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-soft">Tracking number</span>
            <input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Tracking #"
              className="input mt-1 h-9 w-full px-3 text-sm"
              data-testid="seller-order-tracking-input"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-soft">Tracking URL (optional)</span>
            <input
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              placeholder="https://…"
              className="input mt-1 h-9 w-full px-3 text-sm"
              data-testid="seller-order-tracking-url-input"
            />
          </label>
          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={busy != null} className="btn-primary px-4 h-9 text-xs disabled:opacity-50">
              {busy === 'shipped' ? 'Shipping…' : 'Confirm shipped'}
            </button>
            <button type="button" onClick={() => setShowShipForm(false)} className="text-xs font-semibold text-soft hover:text-ink px-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Actions per current status */}
      <div className="mt-4 flex flex-wrap gap-2">
        {status === 'paid' && (
          <button
            type="button"
            disabled={busy != null}
            onClick={() => void advance('processing')}
            data-testid="seller-order-confirm"
            className="btn-primary px-4 h-9 text-xs disabled:opacity-50"
          >
            {busy === 'processing' ? 'Confirming…' : 'Confirm order'}
          </button>
        )}
        {status === 'processing' && !showShipForm && (
          <button
            type="button"
            disabled={busy != null}
            onClick={() => setShowShipForm(true)}
            data-testid="seller-order-ship"
            className="btn-primary px-4 h-9 text-xs disabled:opacity-50"
          >
            Mark shipped
          </button>
        )}
        {status === 'shipped' && (
          <button
            type="button"
            disabled={busy != null}
            onClick={() => void advance('delivered')}
            data-testid="seller-order-deliver"
            className="btn-primary px-4 h-9 text-xs disabled:opacity-50"
          >
            {busy === 'delivered' ? 'Updating…' : 'Mark delivered'}
          </button>
        )}
        {(status === 'delivered' || status === 'cancelled' || status === 'refunded') && (
          <p className="text-xs text-muted">No further action needed on this order.</p>
        )}
        {status === 'pending_payment' && (
          <p className="text-xs text-muted">Waiting on the buyer's payment before you can confirm.</p>
        )}
      </div>
    </div>
  )
}
